import type { Graph, Edge, Node } from '@antv/x6';

/**
 * 检测同一对节点之间的平行边，并为每条边设置垂直偏移顶点，
 * 使 manhattan 路由器走不同路径，避免边线重叠遮挡。
 *
 * 偏移方向垂直于两节点中心连线，多条边均匀分布在连线两侧。
 */
export function applyParallelEdgeOffset(graph: Graph): void {
  const edges = graph.getEdges();

  // 按无方向节点对分组
  const edgeGroups = new Map<string, Edge[]>();

  edges.forEach(edge => {
    const sourceId = edge.getSourceCellId();
    const targetId = edge.getTargetCellId();
    if (!sourceId || !targetId) return;

    // 排序后作为 key，使 A→B 和 B→A 归入同一组
    const key = [sourceId, targetId].sort().join('::');
    const group = edgeGroups.get(key) || [];
    group.push(edge);
    edgeGroups.set(key, group);
  });

  const OFFSET_UNIT = 30; // 每条边间距 30px

  edgeGroups.forEach(group => {
    if (group.length <= 1) return;

    const center = (group.length - 1) / 2;

    group.forEach((edge, index) => {
      const offset = (index - center) * OFFSET_UNIT;
      const sourceNode = graph.getCellById(edge.getSourceCellId()!) as Node;
      const targetNode = graph.getCellById(edge.getTargetCellId()!) as Node;
      if (!sourceNode || !targetNode) return;

      const sourcePort = (edge.getSource?.() as any)?.port;
      const targetPort = (edge.getTarget?.() as any)?.port;
      const edgeData = (edge.getData?.() || {}) as { _mergedEdge?: boolean };
      if (sourcePort === 'port-arrow' || targetPort === 'port-arrow' || edgeData._mergedEdge) {
        // 属性节点箭头或已由 merge 布局过的结构边保持自定义折线
        return;
      }

      const srcParent = sourceNode.getParent?.();
      const tgtParent = targetNode.getParent?.();
      if (srcParent && tgtParent && srcParent.id === tgtParent.id) {
        // 父节点内部的子节点连线保持自定义布局
        return;
      }
      const srcData = sourceNode.getData?.() || {};
      const tgtData = targetNode.getData?.() || {};
      if (srcParent?.isNode?.() && tgtData.isParentCopy && srcParent.id === targetNode.id) {
        // 子节点 → 父副本连线保持自定义布局
        return;
      }
      if (tgtParent?.isNode?.() && srcData.isParentCopy && tgtParent.id === sourceNode.id) {
        // 父副本 → 子节点连线保持自定义布局
        return;
      }

      if (offset === 0) return;

      const sourcePos = sourceNode.getPosition();
      const sourceSize = sourceNode.getSize();
      const targetPos = targetNode.getPosition();
      const targetSize = targetNode.getSize();

      const sourceCenter = {
        x: sourcePos.x + sourceSize.width / 2,
        y: sourcePos.y + sourceSize.height / 2,
      };
      const targetCenter = {
        x: targetPos.x + targetSize.width / 2,
        y: targetPos.y + targetSize.height / 2,
      };

      // 计算垂直方向（顺时针 90 度旋转）
      const dx = targetCenter.x - sourceCenter.x;
      const dy = targetCenter.y - sourceCenter.y;
      const len = Math.sqrt(dx * dx + dy * dy) || 1;
      const perpX = (-dy / len) * offset;
      const perpY = (dx / len) * offset;

      // 设置中间顶点，使 manhattan 路由器绕行偏移
      const midX = (sourceCenter.x + targetCenter.x) / 2 + perpX;
      const midY = (sourceCenter.y + targetCenter.y) / 2 + perpY;

      edge.setVertices([{ x: midX, y: midY }]);
    });
  });
}
